/**
 * MaakOpvolgtaakButton — handmatig een opvolgtaak aanmaken (vangnet).
 *
 * Zit op het gespreksdetail en de klantpagina. POST /api/tasks met
 * source='manual'; sourceRef = call-id indien vanuit een gesprek. Zo kan een
 * medewerker altijd zelf een opvolging op de Opvolgen-lijst zetten, ook als er
 * geen automatische producer aan te pas kwam.
 */

import { useState } from 'react';
import { supabase } from '../../lib/supabase';

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

interface Props {
  sourceRef?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  brand?: string | null;
  /** Voorgevulde titel (bv. "Terugbellen: <klant>"). */
  defaultTitle?: string;
  className?: string;
  onCreated?: () => void;
}

export function MaakOpvolgtaakButton({
  sourceRef,
  customerId,
  customerName,
  brand,
  defaultTitle,
  className,
  onCreated,
}: Props) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(defaultTitle ?? '');
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTitle(defaultTitle ?? '');
    setNote('');
    setDueDate('');
    setError(null);
  };

  const submit = async () => {
    if (!title.trim()) {
      setError('Titel is verplicht');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await fetch('/api/tasks', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          source: 'manual',
          sourceRef: sourceRef ?? undefined,
          customerId: customerId ?? undefined,
          customerName: customerName ?? undefined,
          brand: brand ?? undefined,
          title: title.trim(),
          body: note.trim() || undefined,
          dueAt: dueDate ? new Date(`${dueDate}T09:00:00`).toISOString() : undefined,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `aanmaken mislukt: ${r.status}`);
      }
      setOpen(false);
      reset();
      onCreated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          'px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded'
        }
      >
        Maak opvolgtaak
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          onClick={() => !saving && setOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-slate-900 mb-3">Opvolgtaak maken</h3>
            {error && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{error}</div>
            )}
            <label className="block text-xs font-medium text-slate-600 mb-1">Titel *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bijv. Terugbellen over offerte"
              className="w-full mb-3 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
              autoFocus
            />
            <label className="block text-xs font-medium text-slate-600 mb-1">Notitie</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full mb-3 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <label className="block text-xs font-medium text-slate-600 mb-1">Uiterlijk op (optioneel)</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full mb-4 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => !saving && setOpen(false)}
                className="px-3 py-1.5 text-slate-500 hover:bg-slate-100 text-sm rounded"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={saving}
                className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded"
              >
                {saving ? 'Bezig…' : 'Aanmaken'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
